import axios from 'axios'
import {API_URL} from '../../../../_metronic/helpers'
import {CompanyGateway, GatewayOption, PaymentTransaction, StartedPayment} from './onlinePaymentApi'

export type WalletSummary = {
  available: boolean
  companyId: number
  canCharge?: boolean
  chargeEnabled?: boolean
  balance?: number
  totalCredited?: number
  totalDebited?: number
  smsPartPrice?: number
  /** «پیامک بدون کسر از کیف پول»: an administrator lets this business send buyer SMS without paying. */
  smsWithoutCharge?: boolean
  lowBalanceThreshold?: number
  isLow?: boolean
  minimumCharge?: number
  maximumCharge?: number
  suggestedAmounts?: number[]
  vatRate?: number
  /**
   * Every gateway of the organisation a charge may go through, its default first (backend 1.0.0.703+, user request
   * 2026-10-21); the payer picks one and the others take over in order when it refuses. Absent on an older backend.
   */
  gateways?: GatewayOption[] | null
}

export type WalletEntryView = {
  id: number
  kind: string
  kindTitle: string
  amount: number
  balanceAfter: number
  description?: string | null
  createdAt?: string | null
  invoiceHash?: string | null
}

/**
 * A charge's price: the whole credit, a discount code's discount, VAT on what is left, the payable. `codeError` says why an
 * entered code gives nothing (the quote is then the plain one).
 */
export type ChargeQuote = {
  credit: number
  discount?: number
  discountCode?: string | null
  discountTitle?: string | null
  codeError?: string | null
  vatRate: number
  vat: number
  payable: number
}

export const walletKey = (companyId: number) => ['wallet', companyId]

export const getWallet = async (companyId: number): Promise<WalletSummary> =>
  (await axios.get<WalletSummary>(`${API_URL}/wallet`, {params: {companyId}})).data

export const getWalletEntries = async (
  companyId: number,
  page: number
): Promise<{available: boolean; total: number; page: number; items: WalletEntryView[]}> =>
  (await axios.get(`${API_URL}/wallet/entries`, {params: {companyId, page, pageSize: 15}})).data

export const getWalletCharges = async (companyId: number): Promise<{available: boolean; items: PaymentTransaction[]}> =>
  (await axios.get(`${API_URL}/wallet/charges`, {params: {companyId}})).data

export const getChargeQuote = async (companyId: number, amount: number, code?: string): Promise<ChargeQuote> =>
  (await axios.get<ChargeQuote>(`${API_URL}/wallet/quote`, {params: {companyId, amount, code: code || undefined}})).data

/**
 * Starts a charge through the gateway. returnPath (backend 1.0.0.651+): the panel page to come back to, where the step
 * that was short of credit goes on; without it the payer comes back to the wallet. gatewayId (backend 1.0.0.703+): the
 * gateway the payer picked among the wallet's; left out, the first.
 */
export const startWalletCharge = async (
  companyId: number,
  amount: number,
  code?: string,
  returnPath?: string,
  gatewayId?: number
): Promise<StartedPayment> =>
  (
    await axios.post<StartedPayment>(`${API_URL}/wallet/charge`, {
      companyId,
      amount,
      code: code || undefined,
      returnPath: returnPath || undefined,
      gatewayId: gatewayId || undefined,
    })
  ).data

// ---- A person's own wallet ------------------------------------------------------------------------------
// «کیف پول شخصی» (backend 1.0.0.723, user decision 1405/07/30): a person signed in with a mobile, with or without a
// business, charges it through the organisation's gateway (invoiced in their name) and pays the personal services used
// per use from it. The business wallet's own shape, without a business.

export type PersonalWalletEntryView = {
  id: number
  kind: 'credit' | 'debit'
  amount: number
  balanceAfter: number
  description?: string | null
  createdAt?: string | null
}

export type PersonalWalletSummary = Omit<WalletSummary, 'companyId'> & {
  entries?: PersonalWalletEntryView[]
}

export const personalWalletKey = ['personal-wallet']

export const getPersonalWallet = async (): Promise<PersonalWalletSummary> =>
  (await axios.get<PersonalWalletSummary>(`${API_URL}/personal-wallet`)).data

export const getPersonalChargeQuote = async (amount: number): Promise<ChargeQuote> =>
  (await axios.get<ChargeQuote>(`${API_URL}/personal-wallet/quote`, {params: {amount}})).data

export const startPersonalWalletCharge = async (
  amount: number,
  returnPath?: string,
  gatewayId?: number
): Promise<StartedPayment> =>
  (
    await axios.post<StartedPayment>(`${API_URL}/personal-wallet/charge`, {
      amount,
      returnPath: returnPath || undefined,
      gatewayId: gatewayId || undefined,
    })
  ).data

// ---- System settings (administrators) ------------------------------------------------------------------

export type WalletSystemSettings = {
  isStored: boolean
  isEnabled: boolean
  organizationCompanyId: number
  gatewayId?: number | null
  productServiceId?: number | null
  /** The goods/service of a person's stuffid categories subscription invoice (backend 1.0.0.675+); null: the charge's. */
  stuffidProductServiceId?: number | null
  companyTokenId?: number | null
  sendInvoice: boolean
  invoiceDescription?: string | null
  defaultInvoiceDescription: string
  minimumCharge: number
  maximumCharge: number
  suggestedAmounts: number[]
  smsPartPrice: number
  lowBalanceThreshold: number
  modifiedAt?: string | null
}

export type WalletOrganization = {
  companyId: number
  found: boolean
  name?: string
  economicCode?: string
  gateways?: CompanyGateway[]
  products?: {id: number; name: string; stuffId?: string | null}[]
  fiscalMemories?: {id: number; title?: string | null; uniqueTaxId?: string | null; isProduction: boolean; isDefault: boolean}[]
}

export type WalletAdminData = {available: boolean; settings: WalletSystemSettings; organization: WalletOrganization}

export const getWalletAdminSettings = async (organizationCompanyId?: number): Promise<WalletAdminData> =>
  (await axios.get<WalletAdminData>(`${API_URL}/wallet/admin/settings`, {params: {organizationCompanyId}})).data

export type WalletSettingsInput = Omit<WalletSystemSettings, 'isStored' | 'defaultInvoiceDescription' | 'modifiedAt'>

export const saveWalletAdminSettings = async (input: WalletSettingsInput): Promise<WalletAdminData> =>
  (await axios.put<WalletAdminData>(`${API_URL}/wallet/admin/settings`, input)).data

export type AdminWalletRow = {
  companyId: number
  companyName?: string | null
  balance: number
  totalCredited: number
  totalDebited: number
  modifiedAt?: string | null
}

export const getAdminWallets = async (
  search: string,
  page: number
): Promise<{available: boolean; total: number; page: number; items: AdminWalletRow[]}> =>
  (await axios.get(`${API_URL}/wallet/admin/wallets`, {params: {search: search || undefined, page, pageSize: 20}})).data

export const adjustWallet = async (
  companyId: number,
  amount: number,
  description: string
): Promise<{entry: WalletEntryView; balance: number}> =>
  (await axios.post(`${API_URL}/wallet/admin/adjust`, {companyId, amount, description})).data

// ---- «پیامک بدون کسر از کیف پول» (administrators) --------------------------------------------------------

export type SmsAllowance = {
  companyId: number
  companyName?: string | null
  isEnabled: boolean
  note?: string | null
  modifiedAt?: string | null
  createdAt?: string | null
}

export const smsAllowancesKey = ['wallet-sms-allowances']

export const getSmsAllowances = async (): Promise<{available: boolean; items: SmsAllowance[]}> =>
  (await axios.get(`${API_URL}/wallet/admin/sms-allowances`)).data

export const saveSmsAllowance = async (companyId: number, isEnabled: boolean, note: string): Promise<{item: SmsAllowance}> =>
  (await axios.put(`${API_URL}/wallet/admin/sms-allowances/${companyId}`, {isEnabled, note})).data
